import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';

import { Box, makeStyles, Theme } from '@material-ui/core';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid';

import { GRID_PAGE_SIZE } from '@vyce/core/src/constants';
import { AppA, AppDataGrid, AppChip } from '@vyce/core/src/components';
import { DeviceContext, TeamModuleContext } from '@vyce/core/src/contexts';
import { useTable } from '@vyce/core/src/hooks/useTable';
import { AppSearchInput, AppCheckbox } from '@vyce/core/src/components/inputs';
import { formatSortModel } from '@vyce/core/src/utils/sorting';

import { useBooleanState } from '../../../hooks';
import { NotificationContext } from '../../../contexts/notificationContext';
import { TeamMember } from '../../../types';
import { ModuleCandidatatesSchema } from '../../../api/types';

const chipStyle = { height: 'auto' };

export const useStyles = makeStyles((theme: Theme) => ({
  tableWrapper: {
    '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
      display: 'none',
    },
  },
}));

const defaultSortModel: GridSortModel = [{ field: 'first_name', sort: 'desc' }];

export const EmployeesForTransferList = () => {
  const classes = useStyles();

  const {
    selectionModel,
    setSelectionModel,
    selectedCompanyId,
    teamId,
    getTeamsCandidatesRequest,
  } = useContext(TeamModuleContext);
  const [isLoading, setLoadingTrue, setLoadingFalse] = useBooleanState(false);
  const [employees, setEmployees] = useState<TeamMember[]>([]);
  const [total, setTotal] = useState(0);
  const [innerSelectionModel, setInnerSelectionModel] = useState<string[]>(selectionModel);
  const { sortModel, offset, substring, page, handleSortModelChange, handlePageChange, handleSearchChange } =
    useTable({ defaultSortModel });
  const { handleServerError } = useContext(NotificationContext);
  const { isMobile } = useContext(DeviceContext);

  const handleClickCheckbox = useCallback(
    (id: string) => {
      if (innerSelectionModel.includes(id)) {
        handleSelectionModelChange(innerSelectionModel.filter(item => item !== id));
      } else {
        handleSelectionModelChange([...innerSelectionModel, id]);
      }
    },
    [innerSelectionModel]
  );

  const getNonMembers = useCallback(
    async ({
      offset,
      substring,
      sortModel,
    }: {
      offset: number;
      substring: string;
      sortModel: GridSortModel;
    }) => {
      if (!selectedCompanyId || !teamId) {
        return;
      }
      try {
        setLoadingTrue();

        const requestData: ModuleCandidatatesSchema = {
          offset,
          limit: GRID_PAGE_SIZE,
          substring,
          order_by: formatSortModel<TeamMember>(sortModel),
        };
        const {
          data: { count, items },
        } = await getTeamsCandidatesRequest({
          companyId: selectedCompanyId,
          teamId,
          payload: requestData,
        });
        setTotal(count);
        setEmployees(items);
        setLoadingFalse();
      } catch (e) {
        setLoadingFalse();
        handleServerError(e);
      }
    },
    [teamId, selectedCompanyId, getTeamsCandidatesRequest]
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'first_name',
        headerName: 'Name',
        flex: 2,
        minWidth: 120,
        disableColumnMenu: true,

        renderCell: params => (
          <Box display="flex" alignItems="center" gridGap={8}>
            <AppCheckbox
              onChange={e => {
                handleClickCheckbox(params.row.user_id);
              }}
              color="primary"
              variant="rectangle"
              checked={innerSelectionModel.includes(params.row.user_id)}
            />
            <span>{`${params.row.first_name} ${params.row.last_name}`}</span>
          </Box>
        ),
      },
      {
        field: 'email',
        headerName: 'Email #',
        flex: 1.5,
        minWidth: 270,
        disableColumnMenu: true,
        renderCell: params => (
          <AppA cy-test-id="user-email" content={params.row.email} href={`mailto: ${params.row.email}`} />
        ),
      },
      {
        field: 'phone',
        headerName: 'Phone Number',
        disableColumnMenu: true,
        minWidth: 180,
        renderCell: params => <AppA content={params.row.phone} href={`tel: ${params.row.phone}`} />,
      },
    ],
    [handleClickCheckbox, innerSelectionModel]
  );

  const handleSelectionModelChange = (newSelectionModel: string[]) => {
    setInnerSelectionModel(newSelectionModel);
    setSelectionModel(newSelectionModel);
  };

  const handleClearSelectedWorkers = () => handleSelectionModelChange([]);

  useEffect(() => {
    getNonMembers({ offset, substring, sortModel });
  }, [substring, offset, sortModel, selectedCompanyId]);

  if (!(selectedCompanyId && teamId)) return null;

  return (
    <Box className={classes.tableWrapper}>
      <Box
        mb={2}
        mt={2}
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        alignContent="center"
        gridGap={16}
      >
        <Box flex={1}>
          <AppSearchInput
            cypressId="search-existing-users"
            expanded
            isBorder
            onChange={handleSearchChange}
          />
        </Box>
        {selectionModel.length !== 0 && (
          <AppChip
            style={chipStyle}
            label={`${selectionModel.length} team member${selectionModel.length > 1 ? 's' : ''} selected`}
            onDelete={handleClearSelectedWorkers}
          />
        )}
      </Box>

      <AppDataGrid
        noPaper={true}
        rows={employees}
        getRowId={row => row.user_id}
        columns={columns}
        height="calc(100vh - 430px)"
        rowCount={total}
        loading={isLoading}
        paginationMode="server"
        page={page}
        pageSize={GRID_PAGE_SIZE}
        onPageChange={handlePageChange}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        disableSelectionOnClick
      />
    </Box>
  );
};
